"use client";

import Image from "next/image";
import { useRef } from "react";

import { CONVERGENCE } from "../_lib/event";

/**
 * The registration panel. A soft light tracks the pointer across the halftone
 * field, and the call to action hands the guest over to Luma, which holds the
 * guest list and sends the confirmations.
 */
export function RegistrationCard() {
  const cardRef = useRef<HTMLDivElement>(null);
  const glowRef = useRef<HTMLDivElement>(null);

  function handleMove(event: React.PointerEvent<HTMLDivElement>) {
    const card = cardRef.current;
    const glow = glowRef.current;
    if (!card || !glow) {
      return;
    }
    const rect = card.getBoundingClientRect();
    glow.style.transform = `translate3d(${Math.round(
      event.clientX - rect.left - 260,
    )}px, ${Math.round(event.clientY - rect.top - 260)}px, 0)`;
    glow.style.opacity = "1";
  }

  function handleLeave() {
    const glow = glowRef.current;
    if (glow) {
      glow.style.opacity = "0";
    }
  }

  return (
    <div
      ref={cardRef}
      onPointerMove={handleMove}
      onPointerLeave={handleLeave}
      className="relative isolate overflow-hidden rounded-3xl bg-[#111111]"
    >
      <Image
        src="/marketing/convergence/registration-halftone.png"
        alt=""
        fill
        sizes="(min-width: 1200px) 1200px, 100vw"
        className="object-cover"
      />
      <div
        ref={glowRef}
        aria-hidden="true"
        className="pointer-events-none absolute top-0 left-0 size-[520px] rounded-full opacity-0 transition-opacity duration-500 will-change-transform"
        style={{
          background:
            "radial-gradient(circle, rgba(77,111,255,0.34) 0%, rgba(77,111,255,0.10) 42%, rgba(77,111,255,0) 70%)",
          mixBlendMode: "screen",
        }}
      />
      <div
        className="pointer-events-none absolute inset-y-0 left-0 w-full sm:w-[64%]"
        style={{
          background:
            "linear-gradient(to right, rgba(17,17,17,0.97) 0%, rgba(17,17,17,0.84) 70%, rgba(17,17,17,0) 100%)",
        }}
      />

      <div className="relative px-8 py-14 sm:px-12 sm:py-16">
        <h2
          className="max-w-[18ch] text-[32px] leading-[1.06] font-medium text-white sm:text-[44px]"
          style={{ letterSpacing: "-0.045em" }}
        >
          Spend the day with us
        </h2>
        <p className="mt-5 max-w-[54ch] text-sm leading-6 text-white/65">
          Register on Luma and we&apos;ll follow up with the details.
        </p>

        <a
          href={CONVERGENCE.lumaUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="focus-visible:outline-marketing-blue-light hover:bg-marketing-blue active:bg-marketing-blue-dark group mt-9 inline-flex items-center gap-2.5 rounded-[10px] bg-white px-6 py-3.5 text-[15px] font-medium whitespace-nowrap text-[#111111] transition-[background-color,color] duration-200 hover:text-white focus-visible:outline-2 focus-visible:outline-offset-2 active:text-white"
        >
          Register on Luma
          <svg
            viewBox="0 0 12 12"
            className="size-3 transition-transform duration-200 group-hover:translate-x-0.5"
            aria-hidden="true"
          >
            <path
              d="M2 10L10 2M4 2h6v6"
              stroke="currentColor"
              strokeWidth="1.5"
              fill="none"
            />
          </svg>
        </a>
      </div>
    </div>
  );
}
